import { createLogger } from "@/lib/logger";
/**
 * Interactive Widget Hook
 *
 * Manages overlay visibility for the manually triggered whiteboard exercise
 * (components/InteractiveWidget.tsx, backed by /api/whiteboard/generate) and
 * the A2UI Flow Canvas / Sentence Map widgets.
 */

import type { A2UIMessage } from "@/types/a2ui";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("useInteractiveWidget");

export function useInteractiveWidget() {
  // Exercise widget visibility state
  const [showWidget, setShowWidget] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  // A2UI Flow Canvas state (retained from previous implementation)
  const [flowCanvasMessages, setFlowCanvasMessages] = useState<A2UIMessage[]>([]);
  const [isFlowCanvasLoading, setIsFlowCanvasLoading] = useState(false);
  const [flowCanvasError, setFlowCanvasError] = useState<string | null>(null);
  const [showFlowCanvas, setShowFlowCanvas] = useState(false);
  const [sentenceMapMessages, setSentenceMapMessages] = useState<A2UIMessage[]>([]);
  const [isSentenceMapLoading, setIsSentenceMapLoading] = useState(false);
  const [sentenceMapError, setSentenceMapError] = useState<string | null>(null);
  const [showSentenceMap, setShowSentenceMap] = useState(false);
  const [sentenceMapAnchorKey, setSentenceMapAnchorKey] = useState<string | null>(null);
  const [sentenceMapRequestId, setSentenceMapRequestId] = useState(0);
  const sentenceRequest = useRef(0);
  const sentenceAbort = useRef<AbortController | null>(null);
  const retrySentenceRequest = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      sentenceRequest.current += 1;
      sentenceAbort.current?.abort();
    },
    []
  );

  /**
   * Opens the exercise widget overlay. Generation is handled inside the
   * InteractiveWidget component itself.
   */
  const openWidget = useCallback(() => {
    setShowWidget(true);
    setWidgetError(null);
  }, []);

  const closeWidget = useCallback(() => {
    setShowWidget(false);
    setWidgetError(null);
    setIsWidgetLoading(false);
  }, []);

  // A2UI Flow Canvas generation (retained)
  const generateFlowCanvas = useCallback(
    async (messages: any[], languageName: string, languageLevel: string) => {
      if (messages.length === 0) return;

      setIsFlowCanvasLoading(true);
      setFlowCanvasError(null);
      setFlowCanvasMessages([]);
      setShowFlowCanvas(true);

      try {
        const response = await fetch("/api/widgets/generate-flow-canvas", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: messages.slice(-5),
            language: languageName,
            level: languageLevel,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to generate Flow Canvas (${response.status})`);
        }

        const data = (await response.json()) as { messages: A2UIMessage[]; surfaceId: string };

        if (!data.messages || data.messages.length === 0) {
          throw new Error("No A2UI messages received");
        }

        setFlowCanvasMessages(data.messages);
        logger.info("Flow Canvas generated", { surfaceId: data.surfaceId });
      } catch (err: any) {
        logger.error("Failed to generate Flow Canvas", { error: err });
        setFlowCanvasError("We couldn't create a conversation hint. Please try again.");
      } finally {
        setIsFlowCanvasLoading(false);
      }
    },
    []
  );

  const closeFlowCanvas = useCallback(() => {
    setShowFlowCanvas(false);
    setFlowCanvasMessages([]);
    setFlowCanvasError(null);
  }, []);

  const generateSentenceMap = useCallback(async function requestSentenceMap(
    messages: Array<{ role: string; content: string }>,
    targetLanguage: string,
    instructionLanguage: string,
    languageLevel: string,
    focusedMessage?: { role: string; content: string },
    anchorKey?: string
  ) {
    if (messages.length === 0) return;

    sentenceAbort.current?.abort();
    const controller = new AbortController();
    sentenceAbort.current = controller;
    const request = ++sentenceRequest.current;
    setSentenceMapRequestId(request);
    setSentenceMapAnchorKey(anchorKey ?? null);
    retrySentenceRequest.current = () => {
      void requestSentenceMap(
        messages,
        targetLanguage,
        instructionLanguage,
        languageLevel,
        focusedMessage,
        anchorKey
      );
    };
    setIsSentenceMapLoading(true);
    setSentenceMapError(null);
    setSentenceMapMessages([]);
    setShowSentenceMap(true);

    try {
      const response = await fetch("/api/widgets/generate-sentence-map", {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: messages.slice(-6),
          targetLanguage,
          instructionLanguage,
          languageLevel,
          focusedMessage,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate sentence map (${response.status})`);
      }

      const data = (await response.json()) as { messages: A2UIMessage[]; surfaceId: string };
      if (!data.messages || data.messages.length === 0) {
        throw new Error("No A2UI messages received");
      }

      if (request !== sentenceRequest.current || controller.signal.aborted) return;
      setSentenceMapMessages(data.messages);
      logger.info("Sentence map generated", { surfaceId: data.surfaceId });
    } catch (err: any) {
      if (request !== sentenceRequest.current || controller.signal.aborted) return;
      logger.error("Failed to generate sentence map", { error: err });
      setSentenceMapError("We couldn't create the sentence map. Please try again.");
    } finally {
      if (request === sentenceRequest.current) setIsSentenceMapLoading(false);
    }
  }, []);

  const closeSentenceMap = useCallback(() => {
    sentenceRequest.current += 1;
    sentenceAbort.current?.abort();
    retrySentenceRequest.current = null;
    setSentenceMapAnchorKey(null);
    setIsSentenceMapLoading(false);
    setShowSentenceMap(false);
    setSentenceMapMessages([]);
    setSentenceMapError(null);
  }, []);

  const retrySentenceMap = useCallback(() => retrySentenceRequest.current?.(), []);

  return {
    // Exercise Widget
    showWidget,
    isWidgetLoading,
    widgetError,
    openWidget,
    closeWidget,
    // A2UI Flow Canvas
    flowCanvasMessages,
    isFlowCanvasLoading,
    flowCanvasError,
    showFlowCanvas,
    generateFlowCanvas,
    closeFlowCanvas,
    sentenceMapMessages,
    isSentenceMapLoading,
    sentenceMapError,
    showSentenceMap,
    sentenceMapAnchorKey,
    sentenceMapRequestId,
    generateSentenceMap,
    closeSentenceMap,
    retrySentenceMap,
  };
}
